import React from 'react';

import cn from 'classnames';
import { oneOf } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { faBackwardStep } from '@fortawesome/pro-solid-svg-icons/faBackwardStep';
import { faForwardStep } from '@fortawesome/pro-solid-svg-icons/faForwardStep';
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faVolumeXmark } from '@fortawesome/pro-solid-svg-icons/faVolumeXmark';

import ClickableComponent from 'components/Common/ClickableComponent';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import Tooltip from 'components/Common/Tooltip';
import { ensureArray } from 'utils/arrays';

import { TTS_PLAY_ALL, TTS_STOP } from 'modules/ElementaryCareers/constants';

import useStopPlayingReadAloud from 'modules/ElementaryCareers/hooks/useStopPlayingReadAloud';
import { SET_TTS_DATA } from 'modules/ElementaryCareers/store/actionTypes';

import s from './TextToSpeechPlayAll.css';

const TextToSpeechPlayAll = ({ variant }) => {
  const isModal = variant === 'modal';

  const dispatch = useDispatch();
  const { playingItem, ttsEnabledItems, isPlaying } = useSelector(
    state => state?.textToSpeech,
  );

  const stopPlayingReadAloud = useStopPlayingReadAloud();

  const toggleTts = () => {
    if (isPlaying) {
      stopPlayingReadAloud();
    }
    dispatch({
      type: SET_TTS_DATA,
      payload: { playingItem, ttsOption: TTS_PLAY_ALL, isPlaying: !isPlaying },
    });
  };

  const cancelTts = () => {
    stopPlayingReadAloud();
    dispatch({
      type: SET_TTS_DATA,
      payload: { playingItem: -1, ttsOption: TTS_STOP, isPlaying: false },
    });
  };

  const setNextPlayingItem = () => {
    if (playingItem < ensureArray(ttsEnabledItems).length - 1) {
      stopPlayingReadAloud();
      dispatch({
        type: SET_TTS_DATA,
        payload: { playingItem: playingItem + 1, isPlaying: true },
      });
    }
  };

  const setPrevPlayingItem = () => {
    if (playingItem !== -1) {
      stopPlayingReadAloud();
      dispatch({
        type: SET_TTS_DATA,
        payload: { playingItem: playingItem - 1, isPlaying: true },
      });
    }
  };

  // TODO: update this when we have the new shadow button component
  return (
    <div className={s.root}>
      <Flexbox alignItems="center" className={s.leftItem}>
        <ClickableComponent onClick={setPrevPlayingItem}>
          <FontAwesomeIcon
            icon={faBackwardStep}
            className={cn(s.icon, { [s.iconModal]: isModal })}
          />
        </ClickableComponent>
        <ClickableComponent onClick={toggleTts}>
          <FontAwesomeIcon
            icon={isPlaying ? faPause : faPlay}
            className={cn(s.icon, { [s.iconModal]: isModal })}
          />
        </ClickableComponent>
        <ClickableComponent onClick={setNextPlayingItem}>
          <FontAwesomeIcon
            icon={faForwardStep}
            className={cn(s.icon, { [s.iconModal]: isModal })}
          />
        </ClickableComponent>
      </Flexbox>
      <Tooltip title="Stop reading" placement="bottom">
        <ClickableComponent className={s.cancel} onClick={cancelTts}>
          <FontAwesomeIcon icon={faVolumeXmark} />
        </ClickableComponent>
      </Tooltip>
    </div>
  );
};

TextToSpeechPlayAll.propTypes = {
  variant: oneOf(['modal', 'page']),
};

TextToSpeechPlayAll.defaultProps = {
  variant: 'page',
};

export default TextToSpeechPlayAll;
